/**
 * Creator Library (OutlierOS /explore replica, docs/legacy-ui/01 §3): creators the team published.
 * Header card (DIRECTORY eyebrow, Export Directory, counts, search, Grid/List, Sort, niche chips) → cards
 * with bio + per-creator CSV / list table. Saving adds the creator to Your Feed.
 * Decision 88: the chips are the team's niches (multi-select, any of them), filtered on the server
 * (`GET /api/explore?niches=a,b`); they replace the old client-side category chips.
 * Decision 100: pages of 50; search and sort run on the server too (the stored stats, the same rules as Your Feed).
 * Export Directory downloads every match, read 500 at a time.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { BookmarkPlus, Check, ChevronDown, ChevronLeft, ChevronRight, Compass, Download, LayoutGrid, List, Search } from 'lucide-react';
import { toCsv } from '@vw/core/src/csv';
import { Button, EmptyState, HeaderCard, Input, Segmented, Select, Spinner, StatChip, toast } from '@vw/ui';
import { api, type CreatorCard } from '../api';
import { AnalyzeButton, CreatorCardView, CreatorCell, CreatorTable } from '../components/CreatorGrid';
import { exploreUrl, NicheFilter, type ExploreNiche } from '../components/NicheFilter';
import { downloadText, stamp } from '../download';
import { compact, mult } from '../format';
import { navigate } from '../router';
import { peakOf, usualViewsOf, type CreatorSort } from '../creator-sort';
import { useSession } from '../session';

type Sort = CreatorSort;
const SORTS: Array<{ value: Sort; label: string }> = [
  { value: 'recent', label: 'Recently Scanned' }, { value: 'peak', label: 'Highest Wise Score' }, { value: 'reels', label: 'Most Reels Indexed' },
  { value: 'views', label: 'Highest Usual Views' }, { value: 'transcripts', label: 'Most Free Transcripts' }, { value: 'name', label: 'Alphabetical (A-Z)' },
];
const HEADER = ['Handle', 'Name', 'Followers', 'Usual Views', 'Peak Wise Score', 'Total Reels', 'Free Transcripts', 'Niches', 'Category', 'Bio'];
const row = (c: CreatorCard) => { const peak = peakOf(c); const usual = usualViewsOf(c); return [c.handle ?? '', c.fullName ?? '', c.followers ?? '', usual !== null ? Math.round(usual) : '', peak !== null ? Number(peak.toFixed(2)) : '', c.reelCount, c.transcriptCount ?? 0, (c.niches ?? []).map((n) => n.name).join('; '), c.category ?? '', c.bio ?? '']; };

interface ExploreResponse { creators: CreatorCard[]; niches: ExploreNiche[]; total: number; matched: number; reels: number; page: number; pageSize: number; pages: number }
/** The export reads the directory this many at a time (the API's largest page). */
const EXPORT_PAGE = 500;

export function Explore() {
  const { library, reloadLibrary } = useSession();
  const [data, setData] = useState<ExploreResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [sort, setSort] = useState<Sort>('recent');
  const [page, setPage] = useState(0);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Bumped by Previous/Next so a page that failed to load is asked for again on the next click.
  const [attempt, setAttempt] = useState(0);
  // Typing settles for 300 ms before the server is asked; a new search, sort or niche starts again at page 1.
  useEffect(() => {
    const t = setTimeout(() => { const next = q.trim().replace(/^[@#]/, ''); if (next !== query) { setQuery(next); setPage(0); } }, 300);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickNiches = (next: string[]) => { setPicked(next); setPage(0); };
  const pickSort = (next: Sort) => { setSort(next); setPage(0); };
  // Filter, search, sort and page run on the server; the last answer wins (a slow earlier one never replaces it).
  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    api<ExploreResponse>(exploreUrl(picked, { q: query, sort, page }))
      .then((r) => { if (mine === seq.current) { setData(r); setError(null); } }, (e) => mine === seq.current && setError(e.message))
      .finally(() => mine === seq.current && setLoading(false));
  }, [picked, query, sort, page, attempt]);
  // The library shrank under this page (creators unpublished): go to its last page instead of an empty one.
  useEffect(() => { if (data && data.matched > 0 && data.page >= data.pages) setPage(data.pages - 1); }, [data]);
  const shown = data?.creators ?? null;
  const savedPks = useMemo(() => new Set((library ?? []).map((c) => c.igPk)), [library]);
  // The shell scrolls its <main>, not the window: bring the page's top back into view.
  const top = useRef<HTMLDivElement>(null);
  const goTo = (p: number) => { setPage(p); setAttempt((n) => n + 1); top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  // Every match, in the page order, 500 a time.
  async function exportAll() {
    setExporting(true);
    try {
      const rows: CreatorCard[] = [];
      for (let p = 0, pages = 1; p < pages; p++) {
        const r = await api<ExploreResponse>(exploreUrl(picked, { q: query, sort, page: p, pageSize: EXPORT_PAGE }));
        rows.push(...r.creators);
        pages = r.pages;
      }
      downloadText(`creator-library-${stamp()}.csv`, toCsv(HEADER, rows.map(row)), 'text/csv');
    } catch (e) {
      toast.error('Could not export', { description: (e as Error).message });
    } finally {
      setExporting(false);
    }
  }

  async function save(c: CreatorCard) {
    try {
      await api(`/api/library/${c.igPk}`, { method: 'POST' });
      toast.success(`@${c.handle} added to Your Feed`);
      reloadLibrary();
    } catch (e) {
      toast.error('Could not save', { description: (e as Error).message });
    }
  }
  const saveBtn = (c: CreatorCard) => savedPks.has(c.igPk)
    ? <span title="In Your Feed" className="rounded-xl p-2 text-emerald-500"><Check className="h-4 w-4" /></span>
    : <button title="Save to Your Feed" aria-label={`Save @${c.handle} to Your Feed`} onClick={() => save(c)} className="rounded-xl p-2 text-muted-foreground hover:bg-secondary hover:text-primary"><BookmarkPlus className="h-4 w-4" /></button>;
  const csvBtn = (c: CreatorCard) => (
    <button title="Export CSV" aria-label={`Export @${c.handle} as CSV`} onClick={() => downloadText(`${c.handle}_profile.csv`, toCsv(HEADER, [row(c)]), 'text/csv')} className="rounded-xl p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"><Download className="h-4 w-4" /></button>
  );

  // Before the first answer an error replaces the page; after it, the page (and its chips) stay and the error shows inline.
  if (error && !data) return <p className="text-sm text-rose-500">{error}</p>;
  if (!data || !shown) return <Spinner label="Loading the creator library…" />;
  const filtered = !!(picked.length || query);
  const from = data.page * data.pageSize;

  return (
    <div ref={top} className="mx-auto max-w-[1600px] scroll-mt-6 space-y-6">
      <HeaderCard>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-semibold text-muted-foreground"><span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">Directory</span> • Public catalog</div>
            <h1 className="mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl">Creator Library</h1>
            <p className="mt-1 text-sm text-muted-foreground">Creator profiles published by the team, with their viral benchmarks.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" disabled={!data.matched || exporting} onClick={exportAll}><Download className="h-3.5 w-3.5" />{exporting ? 'Exporting…' : 'Export Directory'}</Button>
            <StatChip value={filtered ? data.matched : data.total} label="Creators" />
            <StatChip value={compact(data.reels)} label="Reels Indexed" />
          </div>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <Input icon={<Search className="h-4 w-4" />} className="flex-1" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search creators by handle, name, bio or niche…" aria-label="Search the creator library" />
          <Segmented value={view} onChange={setView} options={[{ value: 'grid', label: <><LayoutGrid className="h-3.5 w-3.5" />Grid</> }, { value: 'list', label: <><List className="h-3.5 w-3.5" />List</> }]} />
          <label className="relative flex items-center"><span className="sr-only">Sort</span>
            <Select value={sort} onChange={(e) => pickSort(e.target.value as Sort)} className="appearance-none pr-8">{SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</Select>
            <ChevronDown className="pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-muted-foreground" />
          </label>
        </div>
        <NicheFilter niches={data.niches ?? []} total={data.total} selected={picked} onChange={pickNiches} />
        {error && <p role="alert" className="text-sm text-rose-500">Could not load: {error}</p>}
      </HeaderCard>

      {!shown.length ? (
        <EmptyState icon={<Compass className="h-7 w-7" />} title={filtered ? 'No creators match your search' : 'No creators published to the library yet'}
          actions={<Button variant="secondary" onClick={() => navigate('/feed')}>View Your Feed</Button>}>
          {filtered ? 'Try a different handle or clear the niche filter.' : 'Creators appear here once the team publishes them. Your own scans are always in Your Feed.'}
        </EmptyState>
      ) : view === 'grid' ? (
        <div aria-busy={loading} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {shown.map((c) => <CreatorCardView key={c.igPk} c={c} showBio footerExtra={<>{saveBtn(c)}{csvBtn(c)}</>} />)}
        </div>
      ) : (
        <CreatorTable rows={shown} columns={[
          { id: 'creator', label: 'Creator', cell: (c) => <CreatorCell c={c} /> },
          { id: 'followers', label: 'Followers', cell: (c) => <span className="tabular-nums">{compact(c.followers)}</span> },
          { id: 'reels', label: 'Reels Indexed', cell: (c) => <span className="tabular-nums">{c.reelCount}</span> },
          { id: 'peak', label: 'Peak Wise Score', cell: (c) => <span className="font-bold tabular-nums text-amber-600 dark:text-amber-400">{mult(peakOf(c))}</span> },
          { id: 'views', label: 'Usual Views', cell: (c) => <span className="tabular-nums">{compact(c.baselineValue)}</span> },
          { id: 'act', label: 'Actions', cell: (c) => <div className="flex items-center justify-end gap-1"><AnalyzeButton handle={c.handle} />{saveBtn(c)}{csvBtn(c)}</div>, className: 'text-right' },
        ]} />
      )}

      {data.pages > 1 && (
        <nav aria-label="Creator Library pages" className="flex flex-col items-center justify-between gap-3 text-sm sm:flex-row">
          <span className="text-muted-foreground">Showing {from + 1}–{from + shown.length} of {data.matched}</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" disabled={data.page === 0 || loading} onClick={() => goTo(data.page - 1)}><ChevronLeft className="h-3.5 w-3.5" />Previous</Button>
            <span className="tabular-nums text-muted-foreground">Page {data.page + 1} of {data.pages}</span>
            <Button size="sm" variant="secondary" disabled={data.page + 1 >= data.pages || loading} onClick={() => goTo(data.page + 1)}>Next<ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
        </nav>
      )}
    </div>
  );
}
